/**
 * Water painters: open water with rolling wave crests and sparkles, bobbing
 * icebergs, a coral reef seen through clear water, and waterfalls.
 */
import type { IcebergsLayer, ReefLayer, WaterfallLayer, WaterLayer } from '@dropstack/core';
import { clamp, lerp, spreadPositions, wrap, type SceneSize } from '../layout.ts';
import { hash01, range, rngFor } from '../noise.ts';
import {
  circleBlob,
  circlePath,
  ellipsePath,
  gloss,
  glow,
  mix,
  pick,
  rgba,
  rimOf,
  roundRectPath,
  shade,
  strokeRim,
  twinkle,
  vGradient,
  type Circle,
  type Ctx,
  type Point,
} from './draw.ts';
import type { PaintEnv, Painter } from './types.ts';

// ───────────── open water ─────────────

/** Sparkle positions: fixed spots on the water whose brightness pulses. */
function sparkles(ctx: Ctx, top: number, size: SceneSize, seed: number, time: number): void {
  const n = Math.round(clamp((size.width * (size.height - top)) / 9000, 6, 26));
  const rng = rngFor(seed, 41);
  for (let i = 0; i < n; i++) {
    const d = rng.next();
    const x = range(rng, 0, size.width);
    const y = top + (size.height - top) * (0.04 + 0.9 * d * d);
    const rate = range(rng, 0.9, 1.8);
    const phase = range(rng, 0, Math.PI * 2);
    const a = Math.max(0, Math.sin(time * rate + phase)) ** 3;
    twinkle(ctx, x, y, size.unit * (0.008 + 0.012 * d), '#ffffff', a * 0.9);
  }
}

export const waterPainter: Painter<WaterLayer> = {
  extent: (layer, { size }) => ({ top: layer.top * size.height - size.unit * 0.02, bottom: size.height }),
  still(ctx, layer, { size, palette, seed }) {
    const { width, height, bleed, unit } = size;
    const top = layer.top * height;
    const horizon = mix(layer.color, palette.skyBottom, 0.4);
    ctx.fillStyle = vGradient(ctx, top, height, [
      [0, horizon],
      [0.22, layer.color],
      [1, layer.deep],
    ]);
    ctx.fillRect(-bleed - 2, top, width + bleed * 2 + 4, height - top + 2);
    // Glassy reflection streaks, thicker nearer the viewer.
    const rng = rngFor(seed, 43);
    ctx.fillStyle = rgba('#ffffff', 0.16);
    ctx.beginPath();
    for (let i = 0; i < 7; i++) {
      const d = (i + rng.next()) / 7;
      const y = top + (height - top) * (0.06 + 0.85 * d * d);
      ellipsePath(ctx, range(rng, 0, width), y, unit * (0.12 + 0.2 * d), unit * (0.003 + 0.006 * d));
    }
    ctx.fill();
    // Bright horizon line.
    ctx.beginPath();
    ctx.moveTo(-bleed - 2, top);
    ctx.lineTo(width + bleed + 2, top);
    ctx.strokeStyle = rgba(layer.foam, 0.7);
    ctx.lineWidth = Math.max(1, unit * 0.004);
    ctx.stroke();
  },
  moving(ctx, layer, { size, time, seed }) {
    const { width, height, bleed, unit } = size;
    const top = layer.top * height;
    const lively = clamp(layer.waves, 0, 1);
    if (lively > 0) {
      const rows = 3 + Math.round(lively * 6);
      ctx.lineCap = 'round';
      for (let r = 0; r < rows; r++) {
        const d = (r + 0.6) / rows; // 0 near the horizon … 1 at the bottom edge
        const y = top + (height - top) * d ** 1.5;
        const L = unit * (0.025 + 0.075 * d);
        const amp = L * 0.16 * (0.5 + 0.5 * lively);
        const gap = L * (2.8 - lively * 0.6);
        const dir = r % 2 === 0 ? 1 : -1;
        const offset = dir * time * unit * 0.01 * (0.4 + d) + hash01(seed, r) * gap;
        const bob = Math.sin(time * 0.9 + r * 1.3) * amp * 0.35;
        const k0 = Math.floor((-bleed - L - offset) / gap);
        const k1 = Math.ceil((width + bleed + L - offset) / gap);
        ctx.beginPath();
        for (let k = k0; k <= k1; k++) {
          const x = k * gap + offset + (hash01(seed + r + 1, k) - 0.5) * gap * 0.45;
          const yy = y + bob + (hash01(seed + r + 7, k) - 0.5) * L * 0.25;
          // A small "tilde" crest.
          ctx.moveTo(x - L / 2, yy);
          ctx.quadraticCurveTo(x - L / 4, yy - amp * 2, x, yy);
          ctx.quadraticCurveTo(x + L / 4, yy + amp * 2, x + L / 2, yy);
        }
        ctx.strokeStyle = rgba(layer.foam, 0.3 + 0.4 * d);
        ctx.lineWidth = Math.max(1, unit * 0.0035 * (1 + 1.6 * d));
        ctx.stroke();
      }
    }
    if (layer.sparkle) sparkles(ctx, top, size, seed, time);
  },
  animated: (layer) => layer.waves > 0 || layer.sparkle === true,
};

// ───────────── icebergs ─────────────

interface Berg {
  x: number;
  w: number;
  h: number;
  /** Top outline, left to right, relative to (x, waterline). */
  top: Point[];
  phase: number;
}

function planBergs(layer: IcebergsLayer, env: PaintEnv): Berg[] {
  const { size, seed } = env;
  const rng = rngFor(seed, 1);
  const xs = spreadPositions(layer.count, [0.02, 0.98], (i) => hash01(seed + 2, i));
  return xs.map((fx, i) => {
    const s = layer.size * size.unit * range(rng, 0.6, 1.25);
    const w = s * range(rng, 1.1, 1.6);
    const h = s * range(rng, 0.45, 0.8);
    const flat = hash01(seed + 3, i) < 0.35;
    const peakAt = range(rng, -0.25, 0.25);
    const top: Point[] = [{ x: -w / 2, y: 0 }];
    const steps = 6;
    for (let k = 1; k < steps; k++) {
      const u = k / steps - 0.5;
      const rise = flat ? 1 - 0.25 * Math.abs(u) - 0.12 * range(rng, 0, 1) : Math.max(0.2, 1 - Math.abs(u - peakAt) * 2.1) + 0.12 * range(rng, -1, 1);
      top.push({ x: u * w, y: -h * clamp(rise, 0.15, 1) });
    }
    top.push({ x: w / 2, y: 0 });
    return { x: fx * size.width, w, h, top, phase: range(rng, 0, Math.PI * 2) };
  });
}

export const icebergsPainter: Painter<IcebergsLayer> = {
  extent: (layer, { size }) => ({
    top: layer.waterline * size.height - layer.size * size.unit * 1.1,
    bottom: layer.waterline * size.height + layer.size * size.unit * 0.8,
  }),
  moving(ctx, layer, env) {
    const { size, time } = env;
    const waterline = layer.waterline * size.height;
    for (const b of planBergs(layer, env)) {
      const bob = Math.sin(time * 0.6 + b.phase) * size.unit * 0.004;
      const y0 = waterline + bob;
      // Submerged part, seen through the water.
      ctx.beginPath();
      ctx.moveTo(b.x - b.w / 2, y0);
      ctx.quadraticCurveTo(b.x - b.w * 0.55, y0 + b.h * 0.55, b.x - b.w * 0.1, y0 + b.h * 0.8);
      ctx.quadraticCurveTo(b.x + b.w * 0.45, y0 + b.h * 0.7, b.x + b.w / 2, y0);
      ctx.closePath();
      ctx.fillStyle = rgba(layer.shade, 0.4);
      ctx.fill();
      // Above water: a lit face and a shaded right face.
      const pts = b.top.map((p) => ({ x: b.x + p.x, y: y0 + p.y }));
      const outline = () => {
        ctx.beginPath();
        pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
        ctx.closePath();
      };
      outline();
      ctx.fillStyle = vGradient(ctx, y0 - b.h, y0, ['#ffffff', layer.color, mix(layer.color, layer.shade, 0.35)]);
      ctx.fill();
      ctx.save();
      ctx.clip();
      const peak = pts.reduce((best, p) => (p.y < best.y ? p : best));
      ctx.beginPath();
      ctx.moveTo(peak.x, peak.y);
      ctx.lineTo(b.x + b.w * 0.6, y0 - b.h * 1.2);
      ctx.lineTo(b.x + b.w * 0.6, y0 + 2);
      ctx.lineTo(peak.x + b.w * 0.08, y0 + 2);
      ctx.closePath();
      ctx.fillStyle = rgba(layer.shade, 0.75);
      ctx.fill();
      ctx.restore();
      outline();
      ctx.lineJoin = 'round';
      strokeRim(ctx, shade(layer.shade, -0.25), size);
      gloss(ctx, b.x - b.w * 0.2, y0 - b.h * 0.55, b.w * 0.08, b.h * 0.12, 0.7, -0.3);
      // Foam where it meets the water.
      ctx.beginPath();
      ellipsePath(ctx, b.x, y0, b.w * 0.56, size.unit * 0.008);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fill();
    }
  },
};

// ───────────── coral reef ─────────────

type CoralKind = 'branch' | 'fan' | 'brain' | 'tube';
const CORALS: readonly CoralKind[] = ['branch', 'fan', 'brain', 'tube', 'branch', 'fan'];

interface Coral {
  kind: CoralKind;
  x: number;
  y: number;
  s: number;
  color: string;
  seed: number;
}

function planCorals(layer: ReefLayer, env: PaintEnv): Coral[] {
  const { size, seed } = env;
  const rng = rngFor(seed, 1);
  const top = layer.top * size.height;
  const bottom = layer.bottom * size.height;
  const xs = spreadPositions(layer.count, [-0.02, 1.02], (i) => hash01(seed + 5, i));
  const colors = layer.colors.length ? layer.colors : ['#ff8fa3'];
  const corals = xs.map((fx, i): Coral => {
    const near = rng.next(); // 0 = far (up the slope) … 1 = near
    const base = pick(colors, i + Math.floor(near * 3));
    return {
      kind: pick(CORALS, Math.floor(hash01(seed + 7, i) * CORALS.length)),
      x: fx * size.width,
      y: lerp(top + (bottom - top) * 0.35, bottom, near),
      s: size.unit * (0.07 + 0.07 * near),
      // Farther corals fade towards a pale water tint (lighter, not muddier).
      color: mix(base, mix(layer.wash, '#ffffff', 0.45), 0.42 * (1 - near)),
      seed: (seed + i * 101) >>> 0,
    };
  });
  return corals.sort((a, b) => a.y - b.y);
}

function branchCoral(ctx: Ctx, c: Coral, size: SceneSize): void {
  const rng = rngFor(c.seed, 2);
  const segs: [Point, Point, Point][] = [];
  const grow = (from: Point, angle: number, len: number, depth: number) => {
    const to = { x: from.x + Math.cos(angle) * len, y: from.y + Math.sin(angle) * len };
    const ctrl = { x: (from.x + to.x) / 2 + range(rng, -0.2, 0.2) * len, y: (from.y + to.y) / 2 };
    segs.push([from, ctrl, to]);
    if (depth <= 0) return;
    grow(to, angle - range(rng, 0.3, 0.6), len * 0.72, depth - 1);
    grow(to, angle + range(rng, 0.3, 0.6), len * 0.72, depth - 1);
  };
  grow({ x: c.x, y: c.y }, -Math.PI / 2 + range(rng, -0.15, 0.15), c.s * 0.45, 2);
  const path = () => {
    ctx.beginPath();
    for (const [a, q, b] of segs) {
      ctx.moveTo(a.x, a.y);
      ctx.quadraticCurveTo(q.x, q.y, b.x, b.y);
    }
  };
  const w = c.s * 0.12;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  path();
  ctx.strokeStyle = rimOf(c.color);
  ctx.lineWidth = w + size.unit * 0.009;
  ctx.stroke();
  path();
  ctx.strokeStyle = c.color;
  ctx.lineWidth = w;
  ctx.stroke();
  ctx.beginPath();
  for (const [, , b] of segs.slice(-4)) circlePath(ctx, b.x, b.y, w * 0.35);
  ctx.fillStyle = shade(c.color, 0.45);
  ctx.fill();
}

function fanCoral(ctx: Ctx, c: Coral, size: SceneSize): void {
  const r = c.s * 0.6;
  const cy = c.y - c.s * 0.12;
  const fan = () => {
    ctx.beginPath();
    ctx.moveTo(c.x - r, cy);
    ctx.bezierCurveTo(c.x - r * 1.05, cy - r * 1.25, c.x + r * 1.05, cy - r * 1.25, c.x + r, cy);
    ctx.quadraticCurveTo(c.x, cy + r * 0.15, c.x - r, cy);
    ctx.closePath();
  };
  fan();
  ctx.fillStyle = vGradient(ctx, cy - r, cy, [shade(c.color, 0.2), c.color]);
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = rgba(shade(c.color, -0.25), 0.55);
  ctx.lineWidth = Math.max(1, c.s * 0.02);
  ctx.beginPath();
  for (let k = -3; k <= 3; k++) {
    ctx.moveTo(c.x, cy + r * 0.1);
    ctx.lineTo(c.x + k * r * 0.34, cy - r);
  }
  for (const f of [0.35, 0.6, 0.85]) {
    ctx.moveTo(c.x - r, cy - r * f * 0.9);
    ctx.quadraticCurveTo(c.x, cy - r * f * 1.25, c.x + r, cy - r * f * 0.9);
  }
  ctx.stroke();
  ctx.restore();
  fan();
  strokeRim(ctx, rimOf(c.color), size, 0.8);
  ctx.beginPath();
  ctx.moveTo(c.x, cy);
  ctx.lineTo(c.x, c.y);
  ctx.strokeStyle = shade(c.color, -0.3);
  ctx.lineWidth = Math.max(1.5, c.s * 0.05);
  ctx.stroke();
}

function brainCoral(ctx: Ctx, c: Coral, size: SceneSize): void {
  const rx = c.s * 0.5;
  const ry = c.s * 0.36;
  const dome = () => {
    ctx.beginPath();
    ctx.moveTo(c.x - rx, c.y);
    ctx.bezierCurveTo(c.x - rx, c.y - ry * 1.33, c.x + rx, c.y - ry * 1.33, c.x + rx, c.y);
    ctx.closePath();
  };
  dome();
  ctx.fillStyle = vGradient(ctx, c.y - ry, c.y, [shade(c.color, 0.25), c.color, shade(c.color, -0.1)]);
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = rgba(shade(c.color, -0.3), 0.6);
  ctx.lineWidth = Math.max(1, c.s * 0.022);
  ctx.beginPath();
  for (let k = 0; k < 4; k++) {
    const y = c.y - ry * (0.15 + k * 0.22);
    ctx.moveTo(c.x - rx, y);
    for (let x = -rx; x <= rx; x += rx / 4) ctx.quadraticCurveTo(c.x + x + rx / 8, y + (k % 2 ? 1 : -1) * ry * 0.1, c.x + x + rx / 4, y);
  }
  ctx.stroke();
  ctx.restore();
  dome();
  strokeRim(ctx, rimOf(c.color), size, 0.8);
  gloss(ctx, c.x - rx * 0.35, c.y - ry * 0.7, rx * 0.25, ry * 0.12, 0.35);
}

function tubeCoral(ctx: Ctx, c: Coral, size: SceneSize): void {
  const rng = rngFor(c.seed, 3);
  const n = 3 + Math.floor(rng.next() * 3);
  const w = c.s * 0.14;
  for (let k = 0; k < n; k++) {
    const x = c.x + (k - (n - 1) / 2) * w * 1.05;
    const h = c.s * range(rng, 0.45, 1);
    ctx.beginPath();
    roundRectPath(ctx, x - w / 2, c.y - h, w, h, w * 0.4);
    ctx.fillStyle = vGradient(ctx, c.y - h, c.y, [shade(c.color, 0.2), c.color]);
    ctx.fill();
    strokeRim(ctx, rimOf(c.color), size, 0.7);
    ctx.beginPath();
    ellipsePath(ctx, x, c.y - h + w * 0.18, w * 0.32, w * 0.14);
    ctx.fillStyle = shade(c.color, -0.35);
    ctx.fill();
  }
}

const CORAL_PAINTERS: Record<CoralKind, (ctx: Ctx, c: Coral, size: SceneSize) => void> = {
  branch: branchCoral,
  fan: fanCoral,
  brain: brainCoral,
  tube: tubeCoral,
};

function fish(ctx: Ctx, x: number, y: number, len: number, dir: 1 | -1, color: string, wag: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(dir, 1);
  ctx.beginPath();
  ctx.moveTo(-len * 0.35, 0);
  ctx.lineTo(-len * 0.62, -len * 0.2 + wag);
  ctx.lineTo(-len * 0.58, len * 0.2 + wag);
  ctx.closePath();
  ctx.fillStyle = shade(color, -0.1);
  ctx.fill();
  ctx.beginPath();
  ellipsePath(ctx, 0, 0, len * 0.4, len * 0.22);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.fillRect(-len * 0.08, -len * 0.3, len * 0.1, len * 0.6);
  ctx.restore();
  ctx.lineWidth = Math.max(1, len * 0.05);
  ctx.strokeStyle = rimOf(color);
  ctx.stroke();
  ctx.beginPath();
  circlePath(ctx, len * 0.22, -len * 0.04, len * 0.06);
  ctx.fillStyle = '#1d2a44';
  ctx.fill();
  ctx.restore();
}

export const reefPainter: Painter<ReefLayer> = {
  extent: (layer, { size }) => ({ top: layer.top * size.height - size.unit * 0.18, bottom: size.height }),
  still(ctx, layer, env) {
    const { size } = env;
    const top = layer.top * size.height;
    const bottom = layer.bottom * size.height;
    const corals = planCorals(layer, env);
    // Pale sand mounds the corals sit on.
    ctx.beginPath();
    for (const c of corals) ellipsePath(ctx, c.x, c.y + c.s * 0.04, c.s * 0.8, c.s * 0.14);
    ctx.fillStyle = rgba(mix(layer.wash, '#fff4d6', 0.7), 0.7);
    ctx.fill();
    for (const c of corals) CORAL_PAINTERS[c.kind](ctx, c, size);
    // A soft water wash over the whole reef keeps it "under water".
    ctx.fillStyle = vGradient(ctx, top - size.unit * 0.18, bottom, [rgba(layer.wash, 0.3), rgba(layer.wash, 0.08)]);
    ctx.fillRect(-size.bleed - 2, top - size.unit * 0.18, size.width + size.bleed * 2 + 4, bottom - top + size.unit * 0.18);
  },
  moving(ctx, layer, { size, time, seed }) {
    const top = layer.top * size.height;
    const bottom = layer.bottom * size.height;
    // Sea grass tufts rocking in the current.
    const rng = rngFor(seed, 61);
    const tufts = Math.max(3, Math.round(layer.count / 2));
    ctx.lineCap = 'round';
    for (let i = 0; i < tufts; i++) {
      const x = range(rng, 0, size.width);
      const y = lerp(top + (bottom - top) * 0.5, bottom, rng.next());
      const h = size.unit * range(rng, 0.06, 0.12);
      const color = mix('#3fbf7f', layer.wash, 0.35);
      ctx.beginPath();
      for (let k = -2; k <= 2; k++) {
        const sway = Math.sin(time * 0.9 + i + k * 0.4) * h * 0.18;
        ctx.moveTo(x + k * h * 0.06, y);
        ctx.quadraticCurveTo(x + k * h * 0.12 + sway * 0.5, y - h * 0.5, x + k * h * 0.2 + sway, y - h * (0.8 + 0.1 * Math.abs(k)));
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(1.5, size.unit * 0.006);
      ctx.stroke();
    }
    // Little striped fish crossing the reef.
    const n = Math.max(0, Math.floor(layer.fish ?? 0));
    const colors = layer.colors.length ? layer.colors : ['#ffb86b'];
    const frng = rngFor(seed, 67);
    for (let i = 0; i < n; i++) {
      const dir: 1 | -1 = i % 2 === 0 ? 1 : -1;
      const len = size.unit * range(frng, 0.025, 0.04);
      const speed = size.unit * range(frng, 0.02, 0.035);
      const x = wrap(range(frng, 0, size.width) + dir * speed * time, -len, size.width + len);
      const y = lerp(top + (bottom - top) * 0.1, bottom - size.unit * 0.04, frng.next()) + Math.sin(time * 1.3 + i) * size.unit * 0.006;
      fish(ctx, x, y, len, dir, mix(pick(colors, i + 1), layer.wash, 0.15), Math.sin(time * 7 + i) * len * 0.06);
    }
  },
};

// ───────────── waterfall ─────────────

interface Fall {
  cx: number;
  top: number;
  bottom: number;
  w: number;
  /** Width at the bottom (the stream fans out a little). */
  wb: number;
}

function fallOf(layer: WaterfallLayer, size: SceneSize): Fall {
  const w = layer.width * size.unit;
  return { cx: layer.x * size.width, top: layer.top * size.height, bottom: layer.bottom * size.height, w, wb: w * 1.25 };
}

function fallPath(ctx: Ctx, f: Fall): void {
  const lip = f.w * 0.1;
  ctx.beginPath();
  ctx.moveTo(f.cx - f.w / 2, f.top);
  ctx.quadraticCurveTo(f.cx, f.top - lip, f.cx + f.w / 2, f.top);
  ctx.quadraticCurveTo(f.cx + f.w / 2 + lip, f.top + (f.bottom - f.top) * 0.12, f.cx + f.wb / 2, f.bottom);
  ctx.lineTo(f.cx - f.wb / 2, f.bottom);
  ctx.quadraticCurveTo(f.cx - f.w / 2 - lip, f.top + (f.bottom - f.top) * 0.12, f.cx - f.w / 2, f.top);
  ctx.closePath();
}

export const waterfallPainter: Painter<WaterfallLayer> = {
  extent: (layer, { size }) => ({
    top: layer.top * size.height - size.unit * 0.05,
    bottom: layer.bottom * size.height + layer.width * size.unit * 0.8,
  }),
  still(ctx, layer, { size }) {
    const f = fallOf(layer, size);
    fallPath(ctx, f);
    ctx.fillStyle = vGradient(ctx, f.top, f.bottom, [shade(layer.color, 0.25), layer.color, shade(layer.color, 0.1)]);
    ctx.fill();
    ctx.save();
    ctx.clip();
    // Long glassy bands down the fall.
    ctx.fillStyle = 'rgba(255, 255, 255, 0.28)';
    ctx.beginPath();
    for (const u of [-0.3, 0.05, 0.32]) {
      const x0 = f.cx + u * f.w;
      const x1 = f.cx + u * f.wb;
      ctx.moveTo(x0 - f.w * 0.05, f.top);
      ctx.lineTo(x0 + f.w * 0.05, f.top);
      ctx.lineTo(x1 + f.w * 0.06, f.bottom);
      ctx.lineTo(x1 - f.w * 0.06, f.bottom);
      ctx.closePath();
    }
    ctx.fill();
    ctx.restore();
    fallPath(ctx, f);
    strokeRim(ctx, rgba(shade(layer.color, -0.3), 0.7), size, 0.8);
    if (layer.rock) {
      // The ledge the water pours over.
      const lw = f.w * 1.5;
      const lh = size.unit * 0.028;
      ctx.beginPath();
      roundRectPath(ctx, f.cx - lw / 2, f.top - lh * 0.55, lw, lh, lh / 2);
      ctx.fillStyle = vGradient(ctx, f.top - lh, f.top + lh, [shade(layer.rock, 0.15), layer.rock]);
      ctx.fill();
      strokeRim(ctx, rimOf(layer.rock), size);
      ctx.beginPath();
      ctx.moveTo(f.cx - f.w / 2, f.top + lh * 0.3);
      ctx.quadraticCurveTo(f.cx, f.top - lh * 0.1, f.cx + f.w / 2, f.top + lh * 0.3);
      ctx.strokeStyle = rgba(layer.foam, 0.9);
      ctx.lineWidth = Math.max(1.5, lh * 0.35);
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  },
  moving(ctx, layer, { size, time, seed }) {
    const f = fallOf(layer, size);
    const height = f.bottom - f.top;
    // Streaks rushing down (dashes whose offset moves with time).
    ctx.save();
    fallPath(ctx, f);
    ctx.clip();
    ctx.lineCap = 'round';
    const lanes = 6;
    for (let k = 0; k < lanes; k++) {
      const u = (k + 0.5) / lanes - 0.5;
      const dash = height * (0.08 + 0.06 * hash01(seed, k));
      ctx.setLineDash([dash, dash * 1.6]);
      ctx.lineDashOffset = -(time * size.unit * 0.25 + hash01(seed + 1, k) * dash * 3);
      ctx.beginPath();
      ctx.moveTo(f.cx + u * f.w * 0.9, f.top);
      ctx.lineTo(f.cx + u * f.wb * 0.9, f.bottom);
      ctx.strokeStyle = rgba(layer.foam, 0.55);
      ctx.lineWidth = Math.max(1, f.w * 0.06);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.lineDashOffset = 0;
    ctx.restore();
    // Churning foam and mist at the foot.
    glow(ctx, f.cx, f.bottom, f.wb * 1.3, layer.foam, 0.45);
    const puffs = Array.from({ length: 6 }, (_, i): Circle => {
      const u = i / 5 - 0.5;
      const r = f.wb * (0.2 + 0.05 * Math.sin(time * 2.2 + i * 1.7));
      return { x: f.cx + u * f.wb * 1.15, y: f.bottom - r * 0.2 + Math.abs(u) * r * 0.5, r };
    });
    circleBlob(ctx, puffs, layer.foam, rgba(shade(layer.color, -0.15), 0.6), size, 0.6);
  },
};
